import { addNewLineItem } from "@components/smart/smartFastEntryList";
import { ISmartFieldBlur, ISmartFieldChange } from "@components/smart/smartField/SmartField";
import BindingContext, { IEntity } from "@odata/BindingContext";
import {
    ICollectiveAgreementTypeEntity,
    IPrJmhzSubmissionEntity,
    PrJmhzAnnualOverviewEntity,
    PrJmhzEmployeeAnnualTaxSettlementEntity,
    PrJmhzEmployeeEntity,
    PrJmhzEmployeeIncomeOverviewEntity,
    PrJmhzEmployeeIncomeTaxOverviewEntity,
    PrJmhzEmploymentIncomeOverviewEntity
} from "@odata/GeneratedEntityTypes";
import { PrSubmissionSubtypeCode, PrSubmissionTypeCode } from "@odata/GeneratedEnums";
import { PrSubmissionGroup } from "@pages/payroll/submission/Submission.def";
import { setCompanyRelation } from "@pages/payroll/submission/Submission.utils";
import { SubmissionsFormViewForExtend } from "@pages/payroll/submission/SubmissionFormView";
import {
    _annualOverviewPath,
    _employeeIncomeOverviewPath,
    _employmentIncomeOverviewPath
} from "@pages/payroll/submission/submissionTypes/PrJmhzSubmission.def";
import { isInMonths } from "@pages/payroll/submission/submissionTypes/PrJmhzSubmission.utils";

import { withPermissionContext } from "../../../../contexts/permissionContext/withPermissionContext";
import { FormStorage } from "../../../../views/formView/FormStorage";

class PrJmhzSubmissionFormView extends SubmissionsFormViewForExtend<IPrJmhzSubmissionEntity> {
    typeCode = PrSubmissionTypeCode.MonthlyReport;
    subTypeCode: PrSubmissionSubtypeCode = null;
    pathToEmployee: string = null;

    typeSpecificOnAfterLoad(isNew: boolean): Promise<void> {
        const { storage } = this.props;

        const entity = storage.getEntity();
        const promises: Promise<void>[] = [];

        if (isNew) {
            promises.push(setCompanyRelation(storage as unknown as FormStorage, { socialFields: true }));
        }
        this.initCollectiveAgreementType();
        // set local context for showing/hiding fields

        return Promise.all([...promises, super.typeSpecificOnAfterLoad(isNew)]).then();
    }

    handleChange(e: ISmartFieldChange): void {
        const { storage } = this.props;

        super.handleChange(e);
        // keep after storage -> handleChange to have updated data

        this.handleHasOtherSupportingPersonChange(e);
    }

    async handleBlur(args: ISmartFieldBlur): Promise<void> {
        this.calculateNonTaxableIncomeTotal(args);
        this.calculateSupplementsTotal(args);
        this.calculateCompensationsTotal(args);

        return super.handleBlur(args);
    }

    calculateNonTaxableIncomeTotal(e: ISmartFieldBlur): void {
        const { storage } = this.props;
        const path = e.bindingContext.getNavigationPath(true);

        if ([
            _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.EmployerRetirementSavingsContributionTotal),
            _employeeIncomeOverviewPath(PrJmhzEmployeeIncomeOverviewEntity.LongTermCareInsuranceContribution)
        ].includes(path)) {
            const parentBc = e.bindingContext.getParent();

            const nonTaxableIncomeFields = [
                PrJmhzEmployeeIncomeOverviewEntity.EmployerRetirementSavingsContributionTotal,
                PrJmhzEmployeeIncomeOverviewEntity.LongTermCareInsuranceContribution
            ];

            const total = nonTaxableIncomeFields.reduce((sum, field) => {
                const value = storage.getValue(parentBc.navigate(field));
                return sum + (Number.isNaN(value) ? 0 : Number(value));
            }, 0);

            storage.setValue(parentBc.navigate(PrJmhzEmployeeIncomeOverviewEntity.NonTaxableIncomeTotal), total);
        }
    }

    calculateSupplementsTotal(e: ISmartFieldBlur): void {
        const { storage } = this.props;
        const path = e.bindingContext.getNavigationPath(true);

        const fields = [
            PrJmhzEmploymentIncomeOverviewEntity.OvertimeSupplement,
            PrJmhzEmploymentIncomeOverviewEntity.NightWorkSupplement,
            PrJmhzEmploymentIncomeOverviewEntity.WeekendWorkSupplement,
            PrJmhzEmploymentIncomeOverviewEntity.HolidayWorkSupplement
        ];

        if (fields.map(f => _employmentIncomeOverviewPath(f)).includes(path)) {
            const parentBc = e.bindingContext.getParent();

            const total = fields.reduce((sum, field) => {
                const value = storage.getValue(parentBc.navigate(field));
                return sum + (Number.isNaN(value) ? 0 : Number(value));
            }, 0);

            storage.setValue(parentBc.navigate(PrJmhzEmploymentIncomeOverviewEntity.SupplementsTotal), total);
        }
    }

    calculateCompensationsTotal(e: ISmartFieldBlur): void {
        const { storage } = this.props;
        const path = e.bindingContext.getNavigationPath(true);

        const fields = [
            PrJmhzEmploymentIncomeOverviewEntity.VacationCompensation,
            PrJmhzEmploymentIncomeOverviewEntity.HolidayCompensation,
            PrJmhzEmploymentIncomeOverviewEntity.EmployerObstacleCompensation,
            PrJmhzEmploymentIncomeOverviewEntity.EmployeeObstacleCompensation,
            PrJmhzEmploymentIncomeOverviewEntity.TemporaryIncapacityCompensation,
        ];

        if (fields.map(f => _employmentIncomeOverviewPath(f)).includes(path)) {
            const parentBc = e.bindingContext.getParent();

            const total = fields.reduce((sum, field) => {
                const value = storage.getValue(parentBc.navigate(field));
                return sum + (Number.isNaN(value) ? 0 : Number(value));
            }, 0);

            storage.setValue(parentBc.navigate(PrJmhzEmploymentIncomeOverviewEntity.WageCompensationSettled), total);
        }
    }

    handleHasOtherSupportingPersonChange(e: ISmartFieldChange): void {
        const { storage } = this.props;
        const path = e.bindingContext.getPath();

        // incomeTax or AnnualTax -> SupportingPersons
        const supportedIds = [
            PrJmhzEmployeeIncomeTaxOverviewEntity.HasOtherSupportingPerson,
            PrJmhzEmployeeAnnualTaxSettlementEntity.HasSpouseDiscount,
            PrJmhzEmployeeAnnualTaxSettlementEntity.HasChildTaxBenefit
        ] as string[];
        if (supportedIds.includes(path) && e.triggerAdditionalTasks) {
            const parentBc = e.bindingContext.getParent();
            let lineItemsBc: BindingContext;
            let groupId: string = null;
            switch (parentBc.getPath()) {
                case PrJmhzEmployeeEntity.AnnualTaxSettlement:
                    if (path === PrJmhzEmployeeAnnualTaxSettlementEntity.HasOtherSupportingPerson) {
                        groupId = PrSubmissionGroup.AnnualTaxSettlement_2;
                        lineItemsBc = parentBc.navigate(PrJmhzEmployeeAnnualTaxSettlementEntity.SupportingPersons);
                    } else if (path === PrJmhzEmployeeAnnualTaxSettlementEntity.HasSpouseDiscount) {
                        groupId = PrSubmissionGroup.AnnualTaxSettlement;
                        lineItemsBc = parentBc.navigate(PrJmhzEmployeeAnnualTaxSettlementEntity.Partners);
                    } else if (path === PrJmhzEmployeeAnnualTaxSettlementEntity.HasChildTaxBenefit) {
                        groupId = PrSubmissionGroup.AnnualTaxSettlement_3;
                        lineItemsBc = parentBc.navigate(PrJmhzEmployeeAnnualTaxSettlementEntity.SupportedChildren);
                    }
                    break;
                case PrJmhzEmployeeEntity.IncomeTaxOverview:
                    groupId = PrSubmissionGroup.SupportingPersons;
                    lineItemsBc = parentBc.navigate(PrJmhzEmployeeIncomeTaxOverviewEntity.SupportingPersons);
            }
            const value = storage.getValue(e.bindingContext, { useDirectValue: false });
            if (value) {
                const currentValue = storage.getValue(lineItemsBc) as IEntity[];
                if (currentValue?.length) {
                    // already has items, do not add new one
                    return;
                }
                // add item, refresh group -> makes add button visible
                if (groupId) {
                    const def = storage.data.definition.groups.find(g => g.id === groupId);
                    addNewLineItem(storage, lineItemsBc, def);
                }
            } else {
                storage.setValue(lineItemsBc, []);
            }
            storage.refresh(true);
        }
    }

    initCollectiveAgreementType(): void {
        const { storage } = this.props;
        const { bindingContext } = storage.data;
        const typesBc = bindingContext.navigate(_annualOverviewPath(PrJmhzAnnualOverviewEntity.CollectiveAgreementTypes));
        const types = storage.getValue(typesBc) as ICollectiveAgreementTypeEntity[];
        if (!types?.length && isInMonths([12])({ storage })) {
            // add first item to the collection
        }
    }

    onBeforeSave(data?: IPrJmhzSubmissionEntity): IPrJmhzSubmissionEntity {
        data = super.onBeforeSave(data);

        // todo: temporary
        delete data.Detail.AnnualOverview;

        return data;
    }
}

export default withPermissionContext(PrJmhzSubmissionFormView);
